import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Loader2, Search, ShieldAlert, ShieldCheck, UserCog, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

const ROLE_OPTIONS = [
  { value: 'user', label: 'Usuario registrado' },
  { value: 'member', label: 'Socio' },
  { value: 'editor', label: 'Editor' },
  { value: 'admin', label: 'Administrador' },
];

const ROLE_BADGES = {
  superadmin: 'bg-orange-500/10 text-orange-600',
  admin: 'bg-cnib-blue/10 text-cnib-blue',
  editor: 'bg-purple-500/10 text-purple-600',
  member: 'bg-emerald-500/10 text-emerald-600',
  user: 'bg-slate-500/10 text-slate-600',
};

const ROLE_LABELS = {
  superadmin: 'Superadmin',
  admin: 'Administrador',
  editor: 'Editor',
  member: 'Socio',
  user: 'Usuario',
};

const STATUS_BADGES = {
  active: 'bg-emerald-500/10 text-emerald-600',
  suspended: 'bg-red-500/10 text-red-600',
  pending: 'bg-amber-500/10 text-amber-600',
  rejected: 'bg-slate-500/10 text-slate-600',
};

const STATUS_LABELS = {
  active: 'Activo',
  suspended: 'Suspendido',
  pending: 'Pendiente',
  rejected: 'Rechazado',
};

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [updating, setUpdating] = useState(null);
  const [filter, setFilter] = useState(searchParams.get('filter') || 'all');

  const load = () => {
    setLoading(true);
    api
      .get('/admin/users')
      .then(({ data }) => setUsers(data))
      .catch(() => toast.error('No se pudieron cargar los usuarios'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  useEffect(() => {
    if (filter === 'all') setSearchParams({});
    else setSearchParams({ filter });
  }, [filter, setSearchParams]);

  const changeRole = async (userId, role) => {
    setUpdating(userId);
    try {
      await api.patch(`/admin/users/${userId}/role`, { role });
      toast.success('Rol actualizado.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cambiar el rol.');
    } finally {
      setUpdating(null);
    }
  };

  const toggleStatus = async (u) => {
    const status = u.status === 'active' ? 'suspended' : 'active';
    setUpdating(u.user_id);
    try {
      await api.patch(`/admin/users/${u.user_id}/role`, { role: u.role === 'superadmin' ? 'admin' : u.role, status });
      toast.success(status === 'active' ? 'Usuario reactivado.' : 'Usuario suspendido.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cambiar el estado.');
    } finally {
      setUpdating(null);
    }
  };

  const approve = async (u) => {
    if (!window.confirm(`¿Aprobar a ${u.name} como socio activo de CNIB?`)) return;
    setUpdating(u.user_id);
    try {
      await api.post(`/admin/users/${u.user_id}/approve`);
      toast.success(`${u.name} fue aprobado. Ahora es socio activo.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo aprobar el usuario.');
    } finally {
      setUpdating(null);
    }
  };

  const reject = async (u) => {
    if (!window.confirm(`¿Rechazar el registro de ${u.name}? El usuario no podrá acceder.`)) return;
    setUpdating(u.user_id);
    try {
      await api.post(`/admin/users/${u.user_id}/reject`);
      toast.success('Registro rechazado.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo rechazar el usuario.');
    } finally {
      setUpdating(null);
    }
  };

  const pendingCount = useMemo(() => users.filter((u) => u.status === 'pending').length, [users]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return users.filter((u) => {
      if (filter === 'pending' && u.status !== 'pending') return false;
      if (filter === 'active' && u.status !== 'active') return false;
      if (filter === 'suspended' && u.status !== 'suspended') return false;
      if (!term) return true;
      return (
        u.name?.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term)
      );
    });
  }, [users, filter, search]);

  const FILTERS = [
    { id: 'all', label: 'Todos' },
    { id: 'pending', label: `Pendientes${pendingCount > 0 ? ` (${pendingCount})` : ''}` },
    { id: 'active', label: 'Activos' },
    { id: 'suspended', label: 'Suspendidos' },
  ];

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Gestión de accesos</p>
          <h1 className="mt-1 flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">
            <UserCog className="h-7 w-7 text-cnib-blue" /> Usuarios y roles
          </h1>
        </div>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre o correo…"
            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-11 pr-4 text-sm outline-none transition-all focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15 sm:w-72"
            data-testid="admin-users-search"
          />
        </div>
      </div>

      {pendingCount > 0 && filter !== 'pending' && (
        <button
          type="button"
          onClick={() => setFilter('pending')}
          data-testid="admin-users-pending-banner"
          className="flex w-full items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-left transition-colors hover:bg-amber-100"
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500 text-white">
            <Clock className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <p className="text-sm font-extrabold text-amber-900">
              {pendingCount} {pendingCount === 1 ? 'registro pendiente' : 'registros pendientes'} de aprobación
            </p>
            <p className="text-xs text-amber-700">Revisa y aprueba o rechaza las nuevas altas.</p>
          </div>
          <span className="rounded-full bg-amber-500 px-3 py-1 text-xs font-bold text-white">Revisar</span>
        </button>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            data-testid={`admin-users-filter-${f.id}`}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${
              filter === f.id ? 'bg-cnib-navy text-white' : 'bg-white text-slate-600 hover:bg-slate-100'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-cnib-blue" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm" data-testid="admin-users-table">
              <thead className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-3.5">Usuario</th>
                  <th className="px-5 py-3.5">Rol</th>
                  <th className="px-5 py-3.5">Estado</th>
                  <th className="px-5 py-3.5">N° socio</th>
                  <th className="px-5 py-3.5 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((u) => {
                  const isSelf = u.user_id === currentUser?.user_id;
                  const isSuper = u.role === 'superadmin';
                  const locked = isSelf || isSuper;
                  const isPending = u.status === 'pending';
                  return (
                    <tr key={u.user_id} className={`transition-colors hover:bg-slate-50 ${isPending ? 'bg-amber-50/40' : ''}`} data-testid={`admin-user-row-${u.user_id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="h-10 w-10 rounded-full object-cover ring-2 ring-slate-100" />
                          ) : (
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-cnib-navy text-sm font-extrabold text-white">
                              {u.name?.charAt(0) || 'U'}
                            </span>
                          )}
                          <div>
                            <p className="font-bold text-slate-800">{u.name}</p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ${ROLE_BADGES[u.role] || ROLE_BADGES.user}`}>
                          {ROLE_LABELS[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ${STATUS_BADGES[u.status] || STATUS_BADGES.active}`}>
                          {STATUS_LABELS[u.status] || u.status}
                        </span>
                      </td>
                      <td className="px-5 py-4 font-mono text-xs text-slate-500">{u.member_number || '—'}</td>
                      <td className="px-5 py-4">
                        {locked ? (
                          <p className="text-right text-xs font-semibold text-slate-400">
                            {isSelf ? 'Tu cuenta' : 'Protegido'}
                          </p>
                        ) : isPending ? (
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => approve(u)}
                              disabled={updating === u.user_id}
                              data-testid={`admin-user-approve-${u.user_id}`}
                              className="flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-600 disabled:opacity-50"
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" /> Aprobar
                            </button>
                            <button
                              onClick={() => reject(u)}
                              disabled={updating === u.user_id}
                              data-testid={`admin-user-reject-${u.user_id}`}
                              className="flex items-center gap-1.5 rounded-lg bg-red-500/10 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-500/20 disabled:opacity-50"
                            >
                              <XCircle className="h-3.5 w-3.5" /> Rechazar
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2">
                            <select
                              value={u.role}
                              disabled={updating === u.user_id}
                              onChange={(e) => changeRole(u.user_id, e.target.value)}
                              className="rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-slate-700 outline-none focus:border-cnib-blue"
                              data-testid={`admin-user-role-select-${u.user_id}`}
                            >
                              {ROLE_OPTIONS.filter(
                                (opt) => opt.value !== 'admin' || currentUser?.role === 'superadmin'
                              ).map((opt) => (
                                <option key={opt.value} value={opt.value}>
                                  {opt.label}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => toggleStatus(u)}
                              disabled={updating === u.user_id}
                              data-testid={`admin-user-status-toggle-${u.user_id}`}
                              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${
                                u.status === 'active'
                                  ? 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
                                  : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20'
                              }`}
                            >
                              {u.status === 'active' ? (
                                <><ShieldAlert className="h-3.5 w-3.5" /> Suspender</>
                              ) : (
                                <><ShieldCheck className="h-3.5 w-3.5" /> Reactivar</>
                              )}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <p className="py-12 text-center text-sm text-slate-500" data-testid="admin-users-empty">
                {filter === 'pending' ? 'No hay registros pendientes.' : 'Sin resultados para la búsqueda.'}
              </p>
            )}
          </div>
        )}
      </div>

      <p className="text-xs text-slate-500">
        Solo el superadministrador puede asignar el rol de Administrador. Nadie puede modificar su propia cuenta ni la
        del superadministrador. Los nuevos registros deben ser aprobados manualmente antes de poder ingresar.
      </p>
    </div>
  );
}
